import produce from 'immer';
import { values, pickBy, camelCase } from 'lodash';
import { Blueprint, BlueprintRaw } from './dataSlice';

export type AirshipCategory = {
  name: Blueprint['airshipCategory'];
  assetName: string;
  key: 'weapons' | 'bodyArmor' | 'miscArmor' | 'accessories';
  types: Blueprint['type'][];
};

export const airshipCategories: AirshipCategory[] = [
  {
    name: 'Weapons',
    assetName: 'Weapons',
    key: 'weapons',
    types: [
      'Axe',
      'Bow',
      'Catalyst',
      'Crossbow',
      'Dagger',
      'Dual Wield',
      'Gun',
      'Instrument',
      'Mace',
      'Scythe',
      'Spear',
      'Staff',
      'Sword',
      'Wand',
    ],
  },
  {
    name: 'Body Armor',
    assetName: 'Armor',
    key: 'bodyArmor',
    types: ['Clothes', 'Heavy Armor', 'Light Armor'],
  },
  {
    name: 'Misc Armor',
    assetName: 'Armor2',
    key: 'miscArmor',
    types: [
      'Gauntlets',
      'Gloves',
      'Heavy Footwear',
      'Helmet',
      'Light Footwear',
      'Magician Hat',
      'Rogue Hat',
    ],
  },
  {
    name: 'Accessories',
    assetName: 'Accessories',
    key: 'accessories',
    types: [
      'Amulet',
      'Cloak',
      'Dessert',
      'Herbal Medicine',
      'Idol',
      'Meal',
      'Potion',
      'Quiver',
      'Ring',
      'Shield',
      'Spell',
    ],
  },
];

export const getAirshipCategory = (
  type: Blueprint['type']
): Blueprint['airshipCategory'] => {
  const category = airshipCategories.find((c) => c.types.includes(type));
  if (category) {
    return category.name;
  }
  return undefined;
};

const clearThousandSeparator = (str: string) => {
  return str.replace(/\b(?:[0-9]{1,3},[0-9]{3}(?:,[0-9]{3})*)\b/, (match) => {
    return match.replace(/,/g, '');
  });
};

const clearPercentageSign = (str: string) => {
  return str.replace(/^\d+%$/, (match) => {
    return match.replace(/%/g, '');
  });
};

export const transform = (value: string, header: string | number) => {
  if (value === 'Loading...') return 0;
  if (value === '---' || value === '' || header === '' || value === null)
    return undefined;
  return clearThousandSeparator(clearPercentageSign(value));
};

export const cleanObject = <T extends {}>(obj: T) => {
  return pickBy(obj, (val) => val !== '' && val !== undefined) as T;
};

export const cleanObjectsInArray = <T extends {}>(cleanArray: T[]) =>
  cleanArray.map((obj) => cleanObject<T>(obj));

export interface AssetsData {
  asset: string;
  route: string;
  elementAffinity: string;
}

type GetAssetFn = (
  assets: AssetsData[],
  search: string | ((asset: AssetsData) => boolean),
  route: string
) => string | undefined;

export const getAsset: GetAssetFn = (assets, search, route) => {
  const asset = assets.find(
    (a) =>
      (a.route.startsWith(route) || a.route.startsWith(`/Custom${route}`)) &&
      !a.route.endsWith('_d.png') &&
      !a.route.endsWith('_g.png') &&
      (typeof search === 'function' ? search(a) : a.asset === search)
  );
  if (asset) {
    return '/assets' + asset.route;
  }
  return undefined;
};

export type NerileaData = {
  name: string;
  attack: number;
  defense: number;
  health: number;
  evasion: number;
  criticalHitChance: number;
};

export const addAssetsToBlueprints = () => {};

export const combineArrays = (
  kabam: BlueprintRaw[],
  // nerilea: NerileaData[],
  assets: AssetsData[],
  affinityMap: Record<string, string>
) => {
  // console.group('combineArrays');
  // console.log('Kabam blueprints:', JSON.parse(JSON.stringify(kabam)));
  // console.log('Nerilea blueprints:', JSON.parse(JSON.stringify(nerilea)));
  // console.log('Assets:', JSON.parse(JSON.stringify(assets)));
  // console.groupEnd();

  // nerilea = nerilea.filter(
  //   (elem) => elem.name !== 'Fresh Spirit' && elem.name !== "Mundra's Spirit"
  // );

  // const merged = merge(keyBy(kabam, 'name'), keyBy(nerilea, 'name'));
  // const merged = merge(keyBy(nerilea, 'name'), keyBy(kabam, 'name'));

  // let merged;
  // if (kabam.length > nerilea.length) {
  //   console.log('kabam is larger than nerilea, merging kabam first');
  //   merged = merge(keyBy(kabam, 'name'), keyBy(nerilea, 'name'));
  // } else {
  //   console.log('nerilea is larger than kabam, merging nerilea first');
  //   merged = merge(keyBy(nerilea, 'name'), keyBy(kabam, 'name'));
  // }
  // const mergedBlueprints: Blueprint[] = values(merged);

  // Explicitly cast the merged objects to Blueprint[]
  const mergedBlueprints = values(kabam).map((item) => {
    // Create a new object explicitly conforming to the Blueprint type
    const transformedItem: Blueprint = {
      ...item,
      elementAffinity: Array.isArray(item.elementAffinity)
        ? item.elementAffinity
        : typeof item.elementAffinity === 'string'
        ? item.elementAffinity.split(',').map((affinity) => affinity.trim())
        : undefined,
      spiritAffinity: Array.isArray(item.spiritAffinity)
        ? item.spiritAffinity
        : typeof item.spiritAffinity === 'string'
        ? item.spiritAffinity
            .split(',')
            .map((affinity) => affinity.split(' ')[0].trim())
        : undefined,
    };

    return transformedItem;
  });

  return produce(mergedBlueprints, (draft) => {
    draft.forEach((kElm) => {
      // const elem2 = nerilea.find((elem) => elem.name === kElm.name);

      kElm.id = camelCase('t' + kElm.tier + kElm.type + kElm.name);
      kElm.asset = getAsset(assets, kElm.name, '/Items/');

      if (kElm.type === 'Element') {
        kElm.assetEnchantment = getAsset(
          assets,
          kElm.name,
          '/Elements/Enchantment/'
        );
        kElm.elementAffinity = [affinityMap[kElm.name]];
      }
      const searchFn = (asset: AssetsData) =>
        asset.asset.startsWith(kElm.name.split(' ')[0]);
      if (kElm.type === 'Spirit') {
        kElm.assetEnchantment = getAsset(
          assets,
          searchFn,
          '/Skills/Enchantment/'
        );
        kElm.spiritAffinity = [kElm.name.split(' ')[0]];
      }

      const airshipCategory = getAirshipCategory(kElm.type);
      if (airshipCategory) {
        kElm.airshipCategory = airshipCategory;
      } else {
        kElm.airshipPower = 0;
      }

      // Object.assign(kElm, elem2);
      // nerilea = nerilea.filter((elem) => elem.name !== kElm.name);
    });
  });
};
